import {
  LAAG_NAAM,
  LAAG_UITLEG,
  STATUS_TEKST,
  statussenVoorLaag,
  type DashboardStats,
  type KaartStatus,
  type Kaartlaag,
} from '@/types/domain';
import { StatusStip } from '@/components/ui/StatusStip';
import { getalNL } from '@/utils/format';
import './dashboard.css';

interface TegelProps {
  label: string;
  /** Alleen op een breed scherm; op een tablet valt deze weg. */
  kantoor?: boolean;
  waarde: string | number;
  voet?: string;
  /** Zet de stip van de kaart erbij, in dezelfde kleur. */
  status?: KaartStatus;
}

function Tegel({ label, waarde, voet, kantoor, status }: TegelProps): JSX.Element {
  return (
    <div className={`tegel${kantoor ? ' tegel--kantoor' : ''}`}>
      <span className="tegel__label">
        {status && <StatusStip status={status} formaat={10} />}
        {label}
      </span>
      <span className="tegel__waarde">{waarde}</span>
      {voet && <span className="tegel__voet">{voet}</span>}
    </div>
  );
}

/**
 * Eén reeks: alle statussen van één laag, in de volgorde van de kleurtrap.
 *
 * Nulwaarden blijven staan. Een tegel die op nul staat zegt iets — er koppelt
 * niemand níét af — en een reeks waar tegels uit verdwijnen is niet meer op
 * zicht te controleren tegen het totaal.
 */
function Reeks({
  laag,
  telling,
  totaal,
}: {
  laag: Kaartlaag;
  telling: Record<KaartStatus, number>;
  totaal: number;
}): JSX.Element {
  return (
    <section className="tegelgroep">
      <h2 className="tegelgroep__kop">
        {LAAG_NAAM[laag]} ({getalNL(totaal)})
      </h2>
      <p className="tegelgroep__uitleg">{LAAG_UITLEG[laag]}</p>
      <div className="tegels">
        {statussenVoorLaag(laag).map((status) => (
          <Tegel
            key={status}
            status={status}
            label={STATUS_TEKST[laag][status]}
            waarde={getalNL(telling[status])}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * De cijfers, in dezelfde twee lagen als de kaart.
 *
 * Beide reeksen tellen op tot alle woningen in het plangebied, en ze worden
 * geteld met dezelfde functies die de stippen kleuren. Daardoor kan het
 * dashboard de kaart niet meer tegenspreken — en dat gebeurde: "bezocht 76"
 * naast "niet bezocht 4", op hetzelfde scherm.
 *
 * Lees ze als twee vragen over dezelfde woning. De corporatie koppelt een
 * huurpand af terwijl de bewoner zelf nog niets in zijn tuin doet: dat is
 * geen tegenspraak, dat zijn twee antwoorden op twee vragen.
 */
export function StatTegels({ stats }: { stats: DashboardStats }): JSX.Element {
  return (
    <>
      <Reeks laag="afkoppelen" telling={stats.afkoppelen} totaal={stats.totaalWoningen} />
      <Reeks laag="maatregelen" telling={stats.maatregelen} totaal={stats.totaalWoningen} />

      <section className="tegelgroep">
        <h2 className="tegelgroep__kop">Voortgang en opbrengst</h2>
        <div className="tegels">
          <Tegel
            label="Subsidie ingediend"
            waarde={getalNL(stats.subsidieIngediend)}
            voet="Ingediend of al toegekend"
          />
          <Tegel
            label="Berging"
            waarde={`${getalNL(stats.totaleBergingM3, 1)} m³`}
            voet="Indicatief, bij woningen die afkoppelen"
          />
          <Tegel
            label="Meerdere contacten"
            kantoor
            waarde={getalNL(stats.meerdereContacten)}
            voet={`${getalNL(stats.totaalBezoeken)} bezoeken aan de deur in totaal`}
          />
        </div>
      </section>
    </>
  );
}
